// ============================================================================
// NOMINATE CANDIDATE — any facilitator adds someone to the "interested in
// becoming a facilitator" list: a platform user or someone without an account.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Search, X, Loader } from 'lucide-react';
import { nominateCandidate, searchNominableUsers } from '../../services/studio-bookings';

type Mode = 'user' | 'external';
type FoundUser = { userId: string; displayName: string; email: string };

const inputCls = 'w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none';

export default function NominateCandidateModal({ onClose, onNominated }: {
  onClose: () => void;
  onNominated: (name: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('user');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<FoundUser | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode !== 'user' || query.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      searchNominableUsers(query.trim())
        .then(setResults)
        .catch(() => setError('Failed to search users'))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, mode]);

  const canSubmit = mode === 'user' ? !!selected : !!name.trim() && !!email.trim();

  async function handleSubmit() {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const trimmedNotes = notes.trim() || undefined;
      if (mode === 'user') {
        await nominateCandidate({ userId: selected!.userId, notes: trimmedNotes });
        onNominated(selected!.displayName);
      } else {
        await nominateCandidate({ name: name.trim(), email: email.trim(), notes: trimmedNotes });
        onNominated(name.trim());
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to nominate');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md border border-[#e2e8f0]">
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <h2 className="font-bold text-navy text-lg">Nominate a future facilitator</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
            {([['user', 'Platform user'], ['external', 'No account']] as [Mode, string][]).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 text-sm font-semibold transition-all ${
                  mode === m ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'user' ? (
            <div>
              <label className="block text-sm font-semibold text-navy mb-2">Search users</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Type at least 2 characters of a name or email…"
                  value={query}
                  onChange={e => { setQuery(e.target.value); setSelected(null); }}
                  className={`${inputCls} pl-9`}
                />
              </div>
              <div className="mt-2 max-h-44 overflow-y-auto border border-[#e2e8f0] rounded-xl divide-y divide-[#e2e8f0]">
                {searching ? (
                  <div className="flex justify-center py-4"><Loader className="w-4 h-4 animate-spin text-slate-400" /></div>
                ) : results.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {query.trim().length < 2 ? 'Start typing to search' : 'No uncertified users found'}
                  </p>
                ) : results.map(u => (
                  <button
                    key={u.userId}
                    type="button"
                    onClick={() => setSelected(u)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                      selected?.userId === u.userId ? 'bg-primary/10 text-primary' : 'hover:bg-slate-50 text-navy'
                    }`}
                  >
                    <span className="font-semibold">{u.displayName}</span>
                    <span className="ml-2 text-slate-400 text-xs">{u.email}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Name</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-navy mb-2">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} className={inputCls} />
                <p className="mt-1.5 text-xs text-slate-500">Used for training session invites. If they later sign in with this email, their entry links to their account.</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-navy mb-2">Notes <span className="font-normal text-slate-400">(optional)</span></label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={inputCls} />
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-[#e2e8f0] rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || saving}
              className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-all shadow-md shadow-primary/20"
            >
              {saving ? 'Saving…' : 'Nominate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
